// ============================================================================
// Safe Care (Assistance Sociale) - Hooks React Query
// Module : features/assistance_sociale
//
// Hooks TanStack React Query pour les opérations Safe Care.
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSafeCareApi } from '../services/api';
import { StatistiquesSafeCare } from '../types';
import FormatSMS from '../../../Models/FormatSMS';

// ==================== PRODUIT ====================
export function useGetAllProduits() {
  const { produit } = useSafeCareApi();
  return useQuery(['safeCare', 'produits'], () => produit.getAllProduits().then(res => res ?? null).catch(() => null), {
    select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []),
    retry: false,
  });
}

export function useGetProduitById(idProduit?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'produit', idProduit],
    () => produit.getProduitById(idProduit!),
    { enabled: !!idProduit }
  );
}

export function useCreateProduit() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.createProduit, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'produits']),
  });
}

export function useUpdateProduit() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.updateProduit, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'produits']),
  });
}

export function useDeleteProduit() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.deleteProduit, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'produits']),
  });
}

// ==================== VERSION ====================
export function useGetVersionsByProduit(idProduit?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'versions', idProduit],
    () => produit.getVersionsByProduit(idProduit!),
    { enabled: !!idProduit }
  );
}

export function useGetPublishedVersion(idProduit?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'publishedVersion', idProduit],
    () => produit.getPublishedVersion(idProduit!),
    { enabled: !!idProduit }
  );
}

export function useGetComptesClientByPlans(numcli?: string, idVersion?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'comptesClient', numcli, idVersion],
    () => produit.getComptesClientByPlans(numcli!, idVersion!),
    { enabled: !!numcli && !!idVersion }
  );
}

export function useCreateVersion() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.createVersion, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'versions']),
  });
}

export function useUpdateVersion() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.updateVersion, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'versions']),
  });
}

export function usePublierVersion() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(
    (params: { idVersion: number; utiVal: string }) =>
      produit.publierVersion(params.idVersion, params.utiVal),
    {
      onSuccess: () => {
        qc.invalidateQueries(['safeCare', 'versions']);
        qc.invalidateQueries(['safeCare', 'publishedVersion']);
      },
    }
  );
}

// ==================== REGLES ====================
export function useGetEligibilite(idVersion?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'eligibilite', idVersion],
    () => produit.getEligibilite(idVersion!),
    { enabled: !!idVersion }
  );
}

export function useSaveEligibilite() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveEligibilite, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'eligibilite']),
  });
}

export function useGetCotisation(idVersion?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'cotisation', idVersion],
    () => produit.getCotisation(idVersion!),
    { enabled: !!idVersion }
  );
}

export function useSaveCotisation() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveCotisation, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'cotisation']),
  });
}

// ==================== COMPTES DE CONTREPARTIE (COMPTESOPERAT) ====================
// Contrôle ponctuel (appelé à la sélection d'un code opération et avant
// l'enregistrement de la version) : pas de cache.
export function useControleComptesOperat() {
  const { produit } = useSafeCareApi();
  return useMutation(
    ({ codOpe, codAge, typeOperation }: { codOpe: string; codAge: string; typeOperation: 'COTISATION' | 'INDEMNISATION' }) =>
      produit.controleComptesOperat(codOpe, codAge, typeOperation)
  );
}

export function useGetCarence(idVersion?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'carence', idVersion],
    () => produit.getCarence(idVersion!),
    { enabled: !!idVersion }
  );
}

export function useSaveCarence() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveCarence, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'carence']),
  });
}

export function useDeleteCarence() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.deleteCarence, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'carence']),
  });
}

export function useGetInterruption(idVersion?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'interruption', idVersion],
    () => produit.getInterruption(idVersion!),
    { enabled: !!idVersion }
  );
}

export function useSaveInterruption() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveInterruption, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'interruption']),
  });
}

export function useGetParamNotif() {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'paramNotif'],
    () => produit.getParamNotif()
  );
}

export function useSaveParamNotif() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveParamNotif, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'paramNotif']),
  });
}

// Codes opération globaux des frais Safe Care (souscription / réactivation)
export function useGetParamFrais() {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'paramFrais'],
    () => produit.getParamFrais()
  );
}

export function useSaveParamFrais() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveParamFrais, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'paramFrais']),
  });
}

// Aperçu du frais calculé par la grille (affichage ; débit à la validation)
export function useApercuFrais(
  typeFrais: 'SOUSCRIPTION' | 'REACTIVATION',
  ids: { idVersion?: number; idProduit?: number; idSouscription?: number }
) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'apercuFrais', typeFrais, ids.idVersion, ids.idProduit, ids.idSouscription],
    () => produit.apercuFrais(typeFrais, ids),
    { enabled: !!(ids.idVersion || ids.idProduit || ids.idSouscription) }
  );
}

export function useGetRupture(idVersion?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'rupture', idVersion],
    () => produit.getRupture(idVersion!),
    { enabled: !!idVersion }
  );
}

export function useSaveRupture() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveRupture, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'rupture']),
  });
}

export function useGetRupturesAutoEnCours() {
  const { produit } = useSafeCareApi();
  return useQuery(['safeCare', 'rupturesAutoEnCours'], () => produit.getRupturesAutoEnCours());
}

export function useSuspendreRuptureAuto() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(
    (params: { idSouscription: number; motif: string; utiVal: string }) =>
      produit.suspendreRuptureAuto(params.idSouscription, params.motif, params.utiVal),
    { onSuccess: () => qc.invalidateQueries(['safeCare', 'rupturesAutoEnCours']) }
  );
}

// ==================== EVENEMENTS COUVERTS ====================
export function useGetEvenementsByVersion(idVersion?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'evenements', idVersion],
    () => produit.getEvenementsByVersion(idVersion!),
    { enabled: !!idVersion }
  );
}

export function useCreateEvenement() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.createEvenement, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'evenements']),
  });
}

export function useUpdateEvenement() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.updateEvenement, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'evenements']),
  });
}

export function useDeleteEvenement() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.deleteEvenement, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'evenements']),
  });
}

export function useGetRegleEvenement(idEvenement?: number) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'regleEvenement', idEvenement],
    () => produit.getRegleEvenement(idEvenement!),
    { enabled: !!idEvenement }
  );
}

export function useSaveRegleEvenement() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveRegleEvenement, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'regleEvenement']),
  });
}

// ==================== DOCUMENTS REQUIS PAR VERSION (admin) ====================
export function useGetDocumentsRequisByVersion(idVersion?: number, contexte?: string) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'documentsRequis', idVersion, contexte],
    () => produit.getDocumentsRequisByVersion(idVersion!, contexte),
    { enabled: !!idVersion }
  );
}

export function useSaveDocumentRequis() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveDocumentRequis, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'documentsRequis']),
  });
}

export function useDeleteDocumentRequis() {
  const qc = useQueryClient();
  const { produit } = useSafeCareApi();
  return useMutation(produit.deleteDocumentRequis, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'documentsRequis']),
  });
}

// ==================== CATALOGUE SAFE CARE ====================
export function useGetCatalogueProduits() {
  const { produit } = useSafeCareApi();
  return useQuery(['safeCare', 'catalogueProduits'], () => produit.getCatalogueProduits());
}

export function useGetCatalogueEvenements(categorie?: string) {
  const { produit } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'catalogueEvenements', categorie],
    () => produit.getCatalogueEvenements(categorie),
    { enabled: !!categorie }
  );
}

export function useSavePlafond() {
  const { produit } = useSafeCareApi();
  return useMutation(produit.savePlafond);
}

export function useSaveUtilisation() {
  const { produit } = useSafeCareApi();
  return useMutation(produit.saveUtilisation);
}

// ==================== DEMANDE SOUSCRIPTION ====================
export function useGetAllDemandesSouscription(statut?: string) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'demandesSouscription', statut],
    () => souscription.getAllDemandesSouscription(statut).then(res => res ?? null).catch(() => null),
    {
      select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []),
      retry: false,
    }
  );
}

export function useGetDemandeSouscriptionById(idDemande?: number) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'demandeSouscription', idDemande],
    () => souscription.getDemandeSouscriptionById(idDemande!),
    { enabled: !!idDemande }
  );
}

export function useCreateDemandeSouscription() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(souscription.createDemandeSouscription, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'demandesSouscription']),
  });
}

export function useValiderDemandeSouscription() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idDemande: number; utiVal: string }) =>
      souscription.validerDemandeSouscription(params.idDemande, params.utiVal),
    {
      onSuccess: () => {
        qc.invalidateQueries(['safeCare', 'demandesSouscription']);
        qc.invalidateQueries(['safeCare', 'souscriptions']);
      },
    }
  );
}

export function useRejeterDemandeSouscription() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idDemande: number; motifRejet: string; utiVal: string }) =>
      souscription.rejeterDemandeSouscription(params.idDemande, params.motifRejet, params.utiVal),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'demandesSouscription']),
    }
  );
}

export function useAnnulerDemandeSouscription() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idDemande: number; utiSai: string }) =>
      souscription.annulerDemandeSouscription(params.idDemande, params.utiSai),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'demandesSouscription']),
    }
  );
}

export function useRemettreDemandeSouscriptionEnValidation() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idDemande: number; utiSai: string }) =>
      souscription.remettreDemandeSouscriptionEnValidation(params.idDemande, params.utiSai),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'demandesSouscription']),
    }
  );
}

// ==================== SOUSCRIPTION ====================
export function useGetSouscriptionsByClient(numcli?: string) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'souscriptionsByClient', numcli],
    () => souscription.getSouscriptionsByClient(numcli!).then(res => res ?? null).catch(() => null),
    {
      enabled: !!numcli,
      select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []),
      retry: false,
    }
  );
}

export function useGetAllSouscriptions(statut?: string) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'souscriptions', statut],
    () => souscription.getAllSouscriptions(statut).then(res => res ?? null).catch(() => null),
    {
      select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []),
      retry: false,
    }
  );
}

// ==================== RAPPORT / STATISTIQUES ====================
export function useGetStatistiques(dateDebut?: string, dateFin?: string, idProduit?: number) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'statistiques', dateDebut, dateFin, idProduit],
    () => souscription.getStatistiques(dateDebut, dateFin, idProduit).then(res => res ?? null).catch(() => null),
    {
      select: (res): StatistiquesSafeCare | null =>
        res?.StatutCode === 200
          ? {
              parStatut: res.Result?.Table1 ?? [],
              kpi: res.Result?.Table2?.[0] ?? null,
              demandesSouscriptionParStatut: res.Result?.Table3 ?? [],
              parEvenement: res.Result?.Table4 ?? [],
              tendance: res.Result?.Table5 ?? [],
              echeancesParStatut: res.Result?.Table6 ?? [],
              parProduit: res.Result?.Table7 ?? [],
              enDifficulte: res.Result?.Table8 ?? [],
              suspensionsParStatut: res.Result?.Table9 ?? [],
              suspensionsParMotif: res.Result?.Table10 ?? [],
              rachatsParMois: res.Result?.Table11 ?? [],
              personnesParLien: res.Result?.Table12 ?? [],
              syntheseParProduit: res.Result?.Table13 ?? [],
            }
          : null,
      retry: false,
    }
  );
}

// ==================== PERSONNES COUVERTES ====================
export function useGetPersonnesCouvertes(idSouscription?: number) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'personnesCouvertes', idSouscription],
    () => souscription.getPersonnesCouvertes(idSouscription!),
    { enabled: !!idSouscription }
  );
}

export function useAddPersonneCouverte() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(souscription.addPersonneCouverte, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'personnesCouvertes']),
  });
}

export function useDeletePersonneCouverte() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(souscription.deletePersonneCouverte, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'personnesCouvertes']),
  });
}

// ==================== ECHEANCES ====================
export function useGetEcheancesBySouscription(idSouscription?: number) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'echeances', idSouscription],
    () => souscription.getEcheancesBySouscription(idSouscription!),
    { enabled: !!idSouscription }
  );
}

export function useGenererEcheances() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idSouscription: number; utiSai: string }) =>
      souscription.genererEcheances(params.idSouscription, params.utiSai),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'echeances']),
    }
  );
}

export function usePreleverEcheance() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idEcheance: number; codope: string; utiSai: string }) =>
      souscription.preleverEcheance(params.idEcheance, params.codope, params.utiSai),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'echeances']),
    }
  );
}

// ==================== RACHAT ====================
export function useCreateRachat() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(souscription.createRachat, {
    onSuccess: () => {
      qc.invalidateQueries(['safeCare', 'souscriptions']);
      qc.invalidateQueries(['safeCare', 'souscriptionsByClient']);
    },
  });
}

// ==================== INTERRUPTION / SUSPENSION / RESILIATION ====================
export function useGetSouscriptionsImpayees() {
  const { souscription } = useSafeCareApi();
  return useQuery(['safeCare', 'souscriptionsImpayees'], () => souscription.getSouscriptionsImpayees().then(res => res ?? null).catch(() => null), {
    select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []),
    retry: false,
  });
}

export function useSuspendreSouscription() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idSouscription: number; motif: string; utiVal: string }) =>
      souscription.suspendreSouscription(params.idSouscription, params.motif, params.utiVal),
    {
      onSuccess: () => {
        qc.invalidateQueries(['safeCare', 'souscriptionsImpayees']);
        qc.invalidateQueries(['safeCare', 'souscriptions']);
      },
    }
  );
}

export function useAddSuspensionDocument() {
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idSouscription: number; description: string; pieceJointe: string; nomFichier: string; utiSai: string; idDocumentRequis?: number }) =>
      souscription.addSuspensionDocument(params.idSouscription, params.description, params.pieceJointe, params.nomFichier, params.utiSai, params.idDocumentRequis)
  );
}

export function useReactiverSouscription() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idSouscription: number; motif: string; utiVal: string }) =>
      souscription.reactiverSouscription(params.idSouscription, params.motif, params.utiVal),
    {
      onSuccess: () => {
        qc.invalidateQueries(['safeCare', 'souscriptionsImpayees']);
        qc.invalidateQueries(['safeCare', 'souscriptions']);
      },
    }
  );
}

export function useResilierSouscription() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idSouscription: number; motif: string; utiVal: string }) =>
      souscription.resilierSouscription(params.idSouscription, params.motif, params.utiVal),
    {
      onSuccess: () => {
        qc.invalidateQueries(['safeCare', 'souscriptionsImpayees']);
        qc.invalidateQueries(['safeCare', 'souscriptions']);
      },
    }
  );
}

export function useMarquerEcheanceImpayee() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idEcheance: number; utiVal: string }) =>
      souscription.marquerEcheanceImpayee(params.idEcheance, params.utiVal),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'echeances']),
    }
  );
}

// ==================== DEMANDES DE SUSPENSION (flux demande -> validation) ====================
export function useDemanderSuspension() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idSouscription: number; motif: string; utiSai: string }) =>
      souscription.demanderSuspension(params.idSouscription, params.motif, params.utiSai),
    {
      onSuccess: () => {
        qc.invalidateQueries(['safeCare', 'demandesSuspension']);
        qc.invalidateQueries(['safeCare', 'souscriptions']);
      },
    }
  );
}

export function useAddDemandeSuspensionDocument() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idDemandeSuspension: number; description: string; pieceJointe: string; nomFichier: string; utiSai: string; idDocumentRequis?: number }) =>
      souscription.addDemandeSuspensionDocument(params.idDemandeSuspension, params.description, params.pieceJointe, params.nomFichier, params.utiSai, params.idDocumentRequis),
    {
      onSuccess: () => {
        qc.invalidateQueries(['safeCare', 'documentsDemandeSuspension']);
        qc.invalidateQueries(['safeCare', 'demandesSuspension']);
      },
    }
  );
}

// Demande de réactivation : même table DEMANDESUSPENSION, TypeDemande='REACTIVATION'
export function useDemanderReactivation() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idSouscription: number; motif: string; utiSai: string }) =>
      souscription.demanderReactivation(params.idSouscription, params.motif, params.utiSai),
    {
      onSuccess: () => {
        qc.invalidateQueries(['safeCare', 'demandesSuspension']);
        qc.invalidateQueries(['safeCare', 'souscriptions']);
      },
    }
  );
}

export function useGetDemandesSuspension(statut?: string, typeDemande?: string) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'demandesSuspension', statut, typeDemande],
    () => souscription.getDemandesSuspension(statut, typeDemande).then(res => res ?? null).catch(() => null),
    {
      select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []),
      retry: false,
    }
  );
}

export function useGetDocumentsDemandeSuspension(idDemandeSuspension?: number) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'documentsDemandeSuspension', idDemandeSuspension],
    () => souscription.getDocumentsDemandeSuspension(idDemandeSuspension!).then(res => res ?? null).catch(() => null),
    {
      enabled: !!idDemandeSuspension,
      select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []),
      retry: false,
    }
  );
}

export function useValiderDemandeSuspension() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idDemandeSuspension: number; utiVal: string }) =>
      souscription.validerDemandeSuspension(params.idDemandeSuspension, params.utiVal),
    {
      onSuccess: () => {
        qc.invalidateQueries(['safeCare', 'demandesSuspension']);
        qc.invalidateQueries(['safeCare', 'souscriptions']);
      },
    }
  );
}

export function useRejeterDemandeSuspension() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(
    (params: { idDemandeSuspension: number; motifRejet: string; utiVal: string }) =>
      souscription.rejeterDemandeSuspension(params.idDemandeSuspension, params.motifRejet, params.utiVal),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'demandesSuspension']),
    }
  );
}

export function useGetEcheancesImpayeesBySouscription(idSouscription?: number) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'echeancesImpayees', idSouscription],
    () => souscription.getEcheancesImpayeesBySouscription(idSouscription!),
    { enabled: !!idSouscription }
  );
}

export function useGetHistoriqueInterruption(idSouscription?: number) {
  const { souscription } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'historiqueInterruption', idSouscription],
    () => souscription.getHistoriqueInterruption(idSouscription!).then(res => res ?? null).catch(() => null),
    {
      enabled: !!idSouscription,
      select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []),
      retry: false,
    }
  );
}

// ==================== CONTRÔLE AUTOMATIQUE DES IMPAYÉS ====================
export function useControlerImpayes() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(() => souscription.controlerImpayes(), {
    onSuccess: () => {
      qc.invalidateQueries(['safeCare', 'souscriptionsImpayees']);
      qc.invalidateQueries(['safeCare', 'souscriptions']);
    },
  });
}

export function useMarquerEcheancesImpayees() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(() => souscription.marquerEcheancesImpayees(), {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'echeances']),
  });
}

export function useCloturerContratsEchus() {
  const qc = useQueryClient();
  const { souscription } = useSafeCareApi();
  return useMutation(() => souscription.cloturerContratsEchus(), {
    onSuccess: () => {
      qc.invalidateQueries(['safeCare', 'souscriptions']);
      qc.invalidateQueries(['safeCare', 'souscriptionsByClient']);
    },
  });
}

// ==================== DEMANDE ASSISTANCE ====================
export function useGetAllDemandesAssistance(statut?: string) {
  const { assistance } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'demandesAssistance', statut],
    () => assistance.getAllDemandesAssistance(statut).then(res => res ?? null).catch(() => null),
    {
      select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []),
      retry: false,
    }
  );
}

export function useGetDemandeAssistanceById(idDemandeAssistance?: number) {
  const { assistance } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'demandeAssistance', idDemandeAssistance],
    () => assistance.getDemandeAssistanceById(idDemandeAssistance!),
    { enabled: !!idDemandeAssistance }
  );
}

export function useCreateDemandeAssistance() {
  const qc = useQueryClient();
  const { assistance } = useSafeCareApi();
  return useMutation(assistance.createDemandeAssistance, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'demandesAssistance']),
  });
}

export function useGetContexteValidation(idDemandeAssistance?: number) {
  const { assistance } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'contexteValidation', idDemandeAssistance],
    () => assistance.getContexteValidation(idDemandeAssistance!).then(res => res ?? null).catch(() => null),
    {
      enabled: !!idDemandeAssistance,
      select: (res) => (res?.StatutCode === 200 ? res.Result?.Table1?.[0] ?? null : null),
      retry: false,
    }
  );
}

export function useValiderDemandeAssistance() {
  const qc = useQueryClient();
  const { assistance } = useSafeCareApi();
  return useMutation(
    (params: { idDemandeAssistance: number; montantIndemnisation: number; codope: string; utiVal: string }) =>
      assistance.validerDemandeAssistance(
        params.idDemandeAssistance,
        params.montantIndemnisation,
        params.codope,
        params.utiVal
      ),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'demandesAssistance']),
    }
  );
}

export function useRejeterDemandeAssistance() {
  const qc = useQueryClient();
  const { assistance } = useSafeCareApi();
  return useMutation(
    (params: { idDemandeAssistance: number; motifRejet: string; utiVal: string }) =>
      assistance.rejeterDemandeAssistance(params.idDemandeAssistance, params.motifRejet, params.utiVal),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'demandesAssistance']),
    }
  );
}

// ==================== JUSTIFICATIFS ====================
export function useGetJustificatifsByDemande(idDemandeAssistance?: number) {
  const { assistance } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'justificatifs', idDemandeAssistance],
    () => assistance.getJustificatifsByDemande(idDemandeAssistance!),
    { enabled: !!idDemandeAssistance }
  );
}

export function useAddJustificatif() {
  const qc = useQueryClient();
  const { assistance } = useSafeCareApi();
  return useMutation(assistance.addJustificatif, {
    onSuccess: () => qc.invalidateQueries(['safeCare', 'justificatifs']),
  });
}

// ==================== DOCUMENTS REQUIS (côté client) ====================
export function useGetDocumentsRequisBySouscription(idSouscription?: number, contexte: string = 'INDEMNISATION', idEvenement?: number) {
  const { assistance } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'documentsRequisSouscription', idSouscription, contexte, idEvenement],
    () => assistance.getDocumentsRequisBySouscription(idSouscription!, contexte, idEvenement),
    { enabled: !!idSouscription }
  );
}

export function useGetDocumentsRequisManquants(idDemandeAssistance?: number) {
  const { assistance } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'documentsRequisManquants', idDemandeAssistance],
    () => assistance.getDocumentsRequisManquants(idDemandeAssistance!),
    { enabled: !!idDemandeAssistance }
  );
}

// ==================== HISTORIQUE ====================
export function useGetHistorique(typeEntite: string, idEntite?: number) {
  const { assistance } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'historique', typeEntite, idEntite],
    () => assistance.getHistorique(typeEntite, idEntite!),
    { enabled: !!idEntite }
  );
}

// ==================== GABARITS SMS (table FormatSms, référentiel AnyBank) ====================
export function useGetGabaritsSms() {
  const { formatSms } = useSafeCareApi();
  return useQuery(
    ['safeCare', 'gabaritsSms'],
    () => formatSms.getAll().then(res => res ?? null).catch(() => null),
    {
      select: (res): FormatSMS[] =>
        res?.StatutCode === 200 ? (res.Result?.Table ?? res.Result?.Table1 ?? []) : [],
      retry: false,
    }
  );
}

// isNew : true -> CreateFormatSMS, false -> UpdateFormatSMS (clé : ordre / codSmS)
export function useSaveGabaritSms() {
  const qc = useQueryClient();
  const { formatSms } = useSafeCareApi();
  return useMutation(
    (params: { isNew: boolean; gabarit: FormatSMS }) =>
      params.isNew ? formatSms.create(params.gabarit) : formatSms.update(params.gabarit),
    {
      onSuccess: () => qc.invalidateQueries(['safeCare', 'gabaritsSms']),
    }
  );
}
